import React, { useState } from 'react';
import { ArrowRightIcon, StarIcon } from '../icons';
import { Modal } from './Modal';
import { apiService } from '../../services/apiService';
import { friendlyAuthError } from '../../utils/authErrors';
import { InlineAlert } from '../InlineAlert';

interface FeedbackModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmitFeedback = async () => {
        if (isSubmitting) return;

        // Basic validation
        if (!message.trim()) {
            setErrorMessage('Please provide feedback message.');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus('idle');
        setErrorMessage('');

        try {
            const userId = apiService.getStoredUserId();
            if (!userId) {
                setErrorMessage('User session not found. Please log in again.');
                setIsSubmitting(false);
                return;
            }

            const response = await apiService.submitFeedback(userId, {
                category: 'general', // You can expand this to have category selection
                message: message.trim(),
                rating: rating || undefined
            });

            if (response.success) {
                setSubmitStatus('success');
                // Reset form and close modal after showing success
                setTimeout(() => {
                    onClose();
                    setMessage('');
                    setRating(0);
                    setSubmitStatus('idle');
                }, 2000);
            } else {
                setSubmitStatus('error');
                setErrorMessage(friendlyAuthError(response.error, 'Failed to submit feedback. Please try again.'));
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Share Experience">
            <div className="flex flex-col gap-4">
                <h2 className="text-grayscale-1300 font-grotesk text-base font-medium">Tell us more about your overall experience with our platform</h2>
                
                <div className="flex items-center gap-2">
                    {[...Array(5)].map((_, i) => {
                        const ratingValue = i + 1;
                        return (
                            <StarIcon 
                                key={i} 
                                filled={ratingValue <= (hoverRating || rating)}
                                onClick={() => setRating(ratingValue)}
                                onMouseEnter={() => setHoverRating(ratingValue)}
                                onMouseLeave={() => setHoverRating(0)}
                            />
                        )
                    })}
                </div>

                <textarea
                    placeholder="Add message here"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="flex h-24 py-3 px-2.5 items-start gap-2.5 self-stretch rounded-md bg-grayscale-200 text-secondary-text font-grotesk text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />

                {/* Status Messages */}
                {submitStatus === 'success' && (
                    <InlineAlert variant="success" className="mt-4">
                        Thank you for your feedback! 🙏
                    </InlineAlert>
                )}
                {(submitStatus === 'error' || errorMessage) && (
                    <InlineAlert variant="error" className="mt-4">{errorMessage}</InlineAlert>
                )}

                <button 
                    onClick={handleSubmitFeedback}
                    disabled={isSubmitting || submitStatus === 'success'}
                    className="flex py-2.5 px-4 justify-center items-center gap-2 self-stretch rounded-full bg-primary-500 text-grayscale-100 font-grotesk text-sm font-medium disabled:bg-primary-300 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? 'Sending...' : submitStatus === 'success' ? 'Sent!' : 'Send Feedback'} 
                    {!isSubmitting && <ArrowRightIcon />}
                </button>
            </div>
        </Modal>
    );
};